import React, { useEffect } from "react";
import colors from "../../config/colors";
import { ButtonSpacer } from "../Button/styles";
import Icon from "../Icon";
import {
  NumberOfDaysBetweenDates,
  NumberOfDaysBetweenDatesAndCurrentDate,
  dateToUnixTimeStamp,
  formatDateForMeetings,
} from "../utils";
import {
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCellContent,
  TablePagination,
  ResultsLabel,
  ResultsSelect,
  PaginationSpan,
  PreviousButton,
  NextButton,
  PaginationButtons,
  CurrentPage,
  AngleSpan,
} from "./styles";
import Checkbox from "../Checkbox";

/**
 *
 * The classic table component.
 * Takes in columns and rows and renders them as a table.
 */
function PaginatedTable({
  columns,
  rows,
  sortTimeSheet,
  meetingsDashboard,
  meetings,
  exportData,
  setExportCurrentPageData,
  isExport,
  isActive,
  isActiveScroll,
  selectAllHandler,
  allChecked,
}) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const totalPages = Math.ceil(rows?.length / itemsPerPage);

  const paginatedData = rows?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    if (exportData) {
      setExportCurrentPageData(
        exportData?.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        )
      );
    }
  }, [exportData]);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  const [sortOptions, setSortOptions] = React.useState({
    column: null,
    direction: null,
  });
  function sortOnColumn(column) {
    if (column === sortOptions.column && !sortTimeSheet) {
      setSortOptions({
        ...sortOptions,
        direction: sortOptions.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortOptions({
        ...sortOptions,
        column: column,
        direction: !sortTimeSheet ? "asc" : "desc",
      });
    }
  }

  React.useEffect(() => {
    if (sortTimeSheet) {
      sortOnColumn(columns[0].key);
    }
  }, []);

  React.useEffect(() => {
    if (isExport) {
      const preparedExportData = paginatedData.map((row) => {
        const days = row?.meetingScheduleDate
          ? NumberOfDaysBetweenDates([
              row?.acceptedOn,
              dateToUnixTimeStamp(row?.createdAt),
            ])
          : NumberOfDaysBetweenDatesAndCurrentDate([
              dateToUnixTimeStamp(row?.createdAt),
            ]);
        return {
          requestedBy: `${row.user.firstName} ${row.user.lastName}`.replace(
            /,/g,
            ""
          ),
          purpose: row.reasonForMeeting.replace(/,/g, ""),
          days: days >= 0 ? days.replace(/,/g, "") : "-",
          date: formatDateForMeetings(row.createdAt).replace(/,/g, ""),
          acceptedBy:
            `${row.Mentor?.firstName} ${row?.Mentor?.lastName}`.replace(
              /,/g,
              ""
            ),
        };
      });
      // setExportCurrentPageData(preparedExportData);
    }
  }, [itemsPerPage, currentPage]);

  function getValue(obj, path) {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  }

  return (
    <>
      <TableContainer
        meetingsDashboard={meetingsDashboard}
        isActiveScroll={isActiveScroll}
      >
        <TableHead meetingsDashboard={meetingsDashboard}>
          {columns.map((column) => (
            <TableHeaderCell
              isActive={isActive}
              meetingsDashboard={meetingsDashboard}
              key={"header_" + column.key}
              onClick={() => {
                if (column.sortable !== false) {
                  sortOnColumn(column.key);
                }
              }}
            >
              <TableHeaderCellContent
                isActive={isActive}
                meetingsDashboard={meetingsDashboard}
                space={column.checkbox}
              >
                {column.checkbox && (
                  <Checkbox
                    value={allChecked}
                    setValue={(event) => selectAllHandler(event)}
                  />
                )}

                {column.title}

                {sortOptions.column === column.key && (
                  <>
                    <ButtonSpacer />
                    {sortOptions.direction === "asc" ? (
                      <Icon type={"collapse"} color={colors.text} size="10" />
                    ) : (
                      <Icon type={"expand"} color={colors.text} size="10" />
                    )}
                  </>
                )}
              </TableHeaderCellContent>
            </TableHeaderCell>
          ))}
        </TableHead>
        <TableBody>
          {paginatedData
            ?.sort((a, b) => {
              if (!sortOptions.column) return 0;

              const columnDef = columns.find(
                (col) => col.key === sortOptions.column
              );
              const getSortValue =
                columnDef && typeof columnDef.sortValueFn === "function"
                  ? columnDef.sortValueFn
                  : (row) => getValue(row, sortOptions.column); // Safe fallback

              const valueA = getSortValue(a);
              const valueB = getSortValue(b);
              const direction = sortOptions.direction === "asc" ? 1 : -1;

              if (valueA == null && valueB == null) return 0;
              if (valueA == null) return 1;
              if (valueB == null) return -1;

              if (!isNaN(valueA) && !isNaN(valueB)) {
                return direction * (Number(valueA) - Number(valueB));
              }

              if (!isNaN(Date.parse(valueA)) && !isNaN(Date.parse(valueB))) {
                return direction * (new Date(valueA) - new Date(valueB));
              }

              return (
                direction * valueA.toString().localeCompare(valueB.toString())
              );
            })
            .map((row, index) => {
              return (
                <TableRow key={"row_" + index}>
                  {columns.map((column) => (
                    <TableCell
                      isActive={isActive}
                      meetings={meetings}
                      key={"cell_" + column.key + "_" + index}
                      meetingsDashboard={meetingsDashboard}
                    >
                      {column.render ? (
                        <column.render
                          index={index}
                          value={row[column.key]}
                          row={row}
                        >
                          {row[column.key]}
                        </column.render>
                      ) : (
                        row[column.key]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
        </TableBody>
      </TableContainer>
      <TablePagination>
        <div>
          <ResultsLabel>
            Results per page:
            <ResultsSelect
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              {[5, 10, 15, 20].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </ResultsSelect>
          </ResultsLabel>
        </div>
        <PaginationButtons>
          <PaginationSpan>
            <CurrentPage>{currentPage}</CurrentPage> out of {totalPages}
          </PaginationSpan>
          <PreviousButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <AngleSpan>{"<"}</AngleSpan> Previous
          </PreviousButton>

          <NextButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next <AngleSpan next={true}>{">"}</AngleSpan>
          </NextButton>
        </PaginationButtons>
      </TablePagination>
    </>
  );
}
export default PaginatedTable;
